import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight, TrendingUp, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import configs from '../config/config';

const CategorySection = ({ config }) => {
  const navigate = useNavigate();

  // ── Default Config ────────────────────────────────────────────────────────
  const defaultConfig = {
    preset: 'modern-glass',
    title: "Shop by Category",
    subtitle: "Discover what you love",
    categories: [],
    displayMode: 'icon',
    grid: { mobileCols: 2, desktopCols: 5 },
    cardHeight: 400,
    cardBorderRadius: 'rounded-xl',
    cardStyles: {
      background: 'bg-white/80',
      border: 'border border-gray-200/50',
      shadow: 'shadow-lg',
      hoverScale: 'hover:scale-105 hover:shadow-2xl',
      transition: 'transition-all duration-300 ease-out',
    },
    customStyles: {
      container: {
        padding: 'py-8 md:py-12 px-1 sm:px-6 lg:px-8',
        background: 'bg-gradient-to-b from-gray-50 to-white',
        margin: 'mx-auto max-w-7xl',
        textColor: '#111827',
        textAlign: 'left',
        featuredBg: 'bg-gray-400',
        activeBg: 'bg-gray-900',
        fontSize: '2em',
        fontFamily: configs.FONT_FAMILY,
        borderRadius: '10px',
        cardBackground: 'bg-white',
        accentColor: 'green',
      },
    },
    headerAlignment: 'text-center',
    titleStyle: { color: '#111827', fontSize: 'sm:text-xl md:text-2xl lg:text-3xl', fontWeight: 'font-bold' },
    subtitleStyle: { color: '#6B7280', fontSize: 'sm:text-sm md:text-xl lg:text-2xl', fontWeight: 'font-normal' },
    autoScroll: true,
    autoScrollInterval: 1300,
  };

  const finalConfig = {
    ...defaultConfig,
    ...config,
    cardStyles: { ...defaultConfig.cardStyles, ...config?.cardStyles },
    customStyles: { container: { ...defaultConfig.customStyles.container, ...config?.customStyles?.container } },
    grid: { ...defaultConfig.grid, ...config?.grid },
    titleStyle: { ...defaultConfig.titleStyle, ...config?.titleStyle },
    subtitleStyle: { ...defaultConfig.subtitleStyle, ...config?.subtitleStyle },
  };

  const {
    preset,
    title,
    subtitle,
    categories,
    displayMode: globalDisplayMode = 'icon',
    customStyles,
    grid,
    cardHeight,
    cardBorderRadius,
    cardStyles,
    headerAlignment,
    titleStyle,
    subtitleStyle,
    autoScroll = true,
    autoScrollInterval = 1300,
  } = finalConfig;

  const handleNavigate = (slug) => {
    navigate(`/Shop/${slug.toLowerCase()}`);
  };

  // ── Container ─────────────────────────────────────────────────────────────
  const containerClasses = [
    customStyles?.container?.padding,
    customStyles?.container?.margin,
    customStyles?.container?.background || defaultConfig.customStyles.container.background,
  ].filter(Boolean).join(' ');

  const containerStyle = customStyles?.container?.background?.includes('url(') ? {
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
  } : {};

  // ── Header ────────────────────────────────────────────────────────────────
  const Header = () => (
    <div className={`mb-6 md:mb-8 ${headerAlignment}`}>
      <h2
        className={`${titleStyle.fontSize} ${titleStyle.fontWeight}`}
        style={{
          color: titleStyle.color,
          fontFamily: customStyles.container?.fontFamily,
          textAlign: customStyles.container?.textAlign,
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className={`${subtitleStyle.fontSize} ${subtitleStyle.fontWeight} mt-1.5`}
          style={{
            color: subtitleStyle.color,
            fontFamily: customStyles.container?.fontFamily,
            textAlign: customStyles.container?.textAlign,
          }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );

  // ── SHARED CARD STYLES ────────────────────────────────────────────────────
  const commonCardClasses = `${cardBorderRadius} overflow-hidden ${cardStyles.transition} cursor-pointer`;

  // ── Card Variants ─────────────────────────────────────────────────────────
  const OverlayCard = ({ cat }) => (
    <div
      onClick={() => handleNavigate(cat.slug || cat.name)}
      className={`group relative ${commonCardClasses} hover:shadow-xl`}
      style={{ height: `${cardHeight}px` }}
    >
      <img
        src={cat.image || `https://placehold.co/600x600?text=${encodeURIComponent(cat.name)}`}
        alt={cat.name}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-40 group-hover:opacity-90 transition-opacity" />

      {cat.trending && (
        <div className="absolute top-2 right-2 bg-white/95 backdrop-blur-sm px-2 py-1 rounded-full flex items-center gap-1 shadow-md text-xs font-semibold">
          <TrendingUp className="w-3 h-3 text-red-600" />
          <span className="text-gray-800">Hot</span>
        </div>
      )}

      <div className="absolute inset-0 flex flex-col justify-end p-4">
        <h3
          className="font-bold drop-shadow-lg mb-1 line-clamp-2"
          style={{
            color: customStyles.container?.textColor,
            fontSize: customStyles.container?.fontSize,
            fontFamily: customStyles.container?.fontFamily,
          }}
        >
          {cat.name}
        </h3>
        {(cat.count || cat.items) && (
          <p
            className="text-xs md:text-sm mb-3"
            style={{ color: customStyles.container?.accentColor, fontFamily: customStyles.container?.fontFamily }}
          >
            {cat.count || cat.items} Products
          </p>
        )}
        <div
          className={`${customStyles.container?.featuredBg} inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-medium hover:bg-gray-100 transition-all group-hover:gap-2 text-xs md:text-sm w-fit`}
          style={{ color: customStyles.container?.accentColor }}
        >
          Shop Now <ArrowRight className="w-3 h-3 md:w-4 md:h-4" />
        </div>
      </div>
    </div>
  );

  const SplitCard = ({ cat }) => {
    const gradient = cat.color || 'from-purple-600 to-indigo-600';
    return (
      <div
        onClick={() => handleNavigate(cat.slug || cat.name)}
        className={`group ${customStyles.container?.cardBackground} ${commonCardClasses} hover:shadow-xl hover:-translate-y-1`}
        style={{ background: customStyles.container?.cardBackground, height: `${cardHeight}px` }}
      >
        <div className="relative aspect-square overflow-hidden">
          <img
            src={cat.image || `https://placehold.co/500x300?text=${cat.name}`}
            alt={cat.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />

          {cat.trending && (
            <div className="absolute top-2 left-2 bg-red-600 text-white px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1 shadow-md">
              <Sparkles className="w-3 h-3" /> Hot
            </div>
          )}
        </div>

        <div className="p-3 flex flex-col h-1/3">
          <h3
            className="font-bold drop-shadow-lg mb-1 line-clamp-2"
            style={{
              color: customStyles.container?.textColor,
              fontSize: customStyles.container?.fontSize,
              fontFamily: customStyles.container?.fontFamily,
            }}
          >
            {cat.name}
          </h3>
          {(cat.count || cat.items) && (
            <p
              className="text-xs md:text-sm mb-3"
              style={{ color: customStyles.container?.accentColor, fontFamily: customStyles.container?.fontFamily }}
            >
              {cat.count || cat.items} Products
            </p>
          )}
          <button
            className={`mt-auto w-full ${customStyles.container?.featuredBg} text-white py-1.5 rounded-lg font-medium flex items-center justify-center gap-1.5 hover:shadow-md text-xs md:text-sm`}
          >
            Explore <ArrowRight className="w-3 h-3 md:w-4 md:h-4 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    );
  };

  const CircularCard = ({ cat }) => {
    const gradient = cat.color || 'from-indigo-500 via-purple-500 to-pink-500';
    const hasImage = cat.image && cat.image.trim() !== '';

    return (
      <div
        onClick={() => handleNavigate(cat.slug || cat.name)}
        className="group flex flex-col items-center text-center cursor-pointer transition-all duration-300 hover:-translate-y-1 py-2"
      >
        <div className="relative mb-3">
          <div
            className={`w-24 h-24 md:w-32 md:h-32 mx-auto aspect-square rounded-full overflow-hidden shadow-lg transition-all duration-400 group-hover:shadow-xl group-hover:scale-105 ring-2 ring-white group-hover:ring-purple-100 ${hasImage ? '' : `bg-gradient-to-br ${gradient}`}`}
          >
            {hasImage ? (
              <img
                src={cat.image}
                alt={cat.name}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                {cat.icon ? (
                  React.createElement(LucideIcons[cat.icon] || LucideIcons.ShoppingBag, {
                    className: "w-10 h-10 md:w-12 md:h-12 text-white drop-shadow-lg",
                    strokeWidth: 1.5,
                  })
                ) : (
                  <span className="text-4xl md:text-5xl drop-shadow-lg">🛍️</span>
                )}
              </div>
            )}
          </div>
          {cat.trending && (
            <div className="absolute -top-1 -right-1 bg-red-600 text-white w-5 h-5 rounded-full flex items-center justify-center shadow-md">
              <TrendingUp className="w-3 h-3" />
            </div>
          )}
        </div>

        <h3
          className="font-bold drop-shadow-lg mb-1 line-clamp-2"
          style={{
            color: customStyles.container?.textColor,
            fontSize: customStyles.container?.fontSize,
            fontFamily: customStyles.container?.fontFamily,
          }}
        >
          {cat.name}
        </h3>
        {(cat.count || cat.items) && (
          <p
            className="text-xs md:text-sm mb-3"
            style={{ color: customStyles.container?.accentColor, fontFamily: customStyles.container?.fontFamily }}
          >
            {cat.count || cat.items} Products
          </p>
        )}
      </div>
    );
  };

  const BannerCard = ({ cat }) => {
    const gradient = cat.color || 'from-rose-500 to-purple-600';
    return (
      <div
        onClick={() => handleNavigate(cat.slug || cat.name)}
        className={`group relative ${commonCardClasses} hover:shadow-xl aspect-square`}
        style={{ height: '200px' }}
      >
        <img
          src={cat.image || `https://placehold.co/1000x300?text=${cat.name}`}
          alt={cat.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className={`absolute inset-0 bg-gradient-to-r ${gradient} opacity-70 group-hover:opacity-80 transition-opacity`} />

        <div className="absolute inset-0 flex items-center justify-between px-6">
          <div>
            <h3
              className="font-bold drop-shadow-lg mb-1 line-clamp-2"
              style={{
                color: customStyles.container?.textColor,
                fontSize: customStyles.container?.fontSize,
                fontFamily: customStyles.container?.fontFamily,
              }}
            >
              {cat.name}
            </h3>
            {(cat.count || cat.items) && (
              <p
                className="text-xs md:text-sm mb-3"
                style={{ color: customStyles.container?.accentColor, fontFamily: customStyles.container?.fontFamily }}
              >
                {cat.count || cat.items} Products
              </p>
            )}
            <button
              className={`${customStyles.container?.featuredBg} text-gray-900 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-all flex items-center gap-1.5 shadow-md text-xs md:text-sm`}
            >
              Shop Collection <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>

          {cat.trending && (
            <div className="text-right hidden md:block">
              <div className="bg-white/20 backdrop-blur-md px-4 py-2 rounded-xl border border-white/30">
                <Sparkles className="w-6 h-6 text-white mb-1 mx-auto animate-pulse" />
                <span className="text-white font-bold text-xs">Trending</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── DYNAMIC GRID COLUMNS ──────────────────────────────────────────────────
  const getGridClasses = () => {
    const colMap = {
      1: 'grid-cols-1',
      2: 'grid-cols-2',
      3: 'grid-cols-3',
      4: 'grid-cols-4',
      5: 'grid-cols-5',
      6: 'grid-cols-6',
    };

    const mobile = colMap[grid.mobileCols] || 'grid-cols-2';
    const desktop = colMap[grid.desktopCols] || 'grid-cols-4';

    return `${mobile} md:${desktop} gap-4 md:gap-6`;
  };

  // ── CAROUSEL WRAPPER ──────────────────────────────────────────────────────
  const CarouselWrapper = ({ CardComponent }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [isTransitioning, setIsTransitioning] = useState(false);

    // Touch / swipe state
    const touchStartX = useRef(null);
    const touchStartY = useRef(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(null);
    const [dragOffset, setDragOffset] = useState(0);

    const getItemsPerView = () => {
      if (typeof window === 'undefined') return 4;
      if (window.innerWidth < 640) return grid.mobileCols || 2;
      return grid.desktopCols || 5;
    };

    const [itemsPerView, setItemsPerView] = useState(getItemsPerView());

    useEffect(() => {
      const handleResize = () => setItemsPerView(getItemsPerView());
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, [grid.mobileCols, grid.desktopCols]);

    const maxIndex = Math.max(0, categories.length - itemsPerView);

    const next = useCallback(() => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
      setTimeout(() => setIsTransitioning(false), 420);
    }, [isTransitioning, maxIndex]);

    const prev = useCallback(() => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
      setTimeout(() => setIsTransitioning(false), 420);
    }, [isTransitioning, maxIndex]);

    // ── Auto-scroll ───────────────────────────────────────────────────────
    useEffect(() => {
      if (!autoScroll || isPaused || categories.length <= itemsPerView) return;
      const interval = setInterval(next, autoScrollInterval);
      return () => clearInterval(interval);
    }, [autoScroll, autoScrollInterval, isPaused, currentIndex, itemsPerView, next]);

    // ── Touch handlers ────────────────────────────────────────────────────
    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      setIsPaused(true);
    };

    const handleTouchMove = (e) => {
      if (touchStartX.current === null) return;
      const dx = e.touches[0].clientX - touchStartX.current;
      const dy = e.touches[0].clientY - touchStartY.current;
      // Only hijack horizontal swipes
      if (Math.abs(dx) > Math.abs(dy)) {
        e.preventDefault();
        setDragOffset(dx);
      }
    };

    const handleTouchEnd = (e) => {
      const dx = e.changedTouches[0].clientX - (touchStartX.current || 0);
      const threshold = 50;
      if (Math.abs(dx) > threshold) {
        dx < 0 ? next() : prev();
      }
      touchStartX.current = null;
      touchStartY.current = null;
      setDragOffset(0);
      setTimeout(() => setIsPaused(false), 800);
    };

    // ── Mouse drag handlers ───────────────────────────────────────────────
    const handleMouseDown = (e) => {
      isDragging.current = true;
      dragStartX.current = e.clientX;
      setIsPaused(true);
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      setDragOffset(e.clientX - dragStartX.current);
    };

    const handleMouseUp = (e) => {
      if (!isDragging.current) return;
      isDragging.current = false;
      const dx = e.clientX - (dragStartX.current || 0);
      const threshold = 50;
      if (Math.abs(dx) > threshold) {
        dx < 0 ? next() : prev();
      }
      dragStartX.current = null;
      setDragOffset(0);
      setTimeout(() => setIsPaused(false), 800);
    };

    const handleMouseLeave = () => {
      if (isDragging.current) {
        isDragging.current = false;
        dragStartX.current = null;
        setDragOffset(0);
      }
      setIsPaused(false);
    };

    const totalDots = Math.ceil(categories.length / itemsPerView);
    const currentDot = Math.floor(currentIndex / itemsPerView);

    // Base translate % + live drag offset in px
    const baseTranslate = (currentIndex * 100) / itemsPerView;

    if (categories.length === 0) return null;

    return (
      <div
        className="relative select-none"
        onMouseEnter={() => !isDragging.current && setIsPaused(true)}
        onMouseLeave={handleMouseLeave}
      >
        <Header />

        <div
          className="relative overflow-hidden cursor-grab active:cursor-grabbing"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
        >
          <div
            className="flex"
            style={{
              transform: `translateX(calc(-${baseTranslate}% + ${dragOffset}px))`,
              transition: dragOffset !== 0 ? 'none' : 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              willChange: 'transform',
            }}
          >
            {categories.map((cat, idx) => (
              <div
                key={cat.id || cat.name || idx}
                className="flex-shrink-0 px-2"
                style={{ width: `${100 / itemsPerView}%` }}
                // Prevent card click firing after a drag
                onClick={(e) => { if (Math.abs(dragOffset) > 5) e.stopPropagation(); }}
              >
                <CardComponent cat={cat} />
              </div>
            ))}
          </div>
        </div>

        {categories.length > itemsPerView && (
          <>
            <button
              onClick={prev}
              disabled={isTransitioning}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 bg-white/95 backdrop-blur-sm p-2 md:p-2.5 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-gray-200/50 disabled:opacity-50"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
            </button>
            <button
              onClick={next}
              disabled={isTransitioning}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 bg-white/95 backdrop-blur-sm p-2 md:p-2.5 rounded-full shadow-lg hover:bg-white hover:scale-110 transition-all duration-200 z-10 border border-gray-200/50 disabled:opacity-50"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4 md:w-5 md:h-5 text-gray-800" />
            </button>

            {totalDots > 1 && (
              <div className="flex justify-center gap-1.5 mt-6">
                {Array.from({ length: totalDots }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (!isTransitioning) {
                        setIsTransitioning(true);
                        setCurrentIndex(idx * itemsPerView);
                        setTimeout(() => setIsTransitioning(false), 420);
                      }
                    }}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      idx === currentDot ? 'bg-indigo-600 w-6' : 'bg-gray-300 w-1.5 hover:bg-gray-400'
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  // ── STATIC GRID ───────────────────────────────────────────────────────────
  const StaticGrid = ({ CardComponent }) => {
    if (categories.length === 0) return null;

    return (
      <div>
        <Header />
        <div className={getGridClasses()}>
          {categories.map((cat, idx) => (
            <CardComponent key={cat.id || cat.name || idx} cat={cat} />
          ))}
        </div>
      </div>
    );
  };

  // ── Render Logic ──────────────────────────────────────────────────────────
  const renderContent = () => {
    const isGridMode = preset === 'clean-grid' || preset === 'card-grid';

    if (isGridMode) {
      return <StaticGrid CardComponent={SplitCard} />;
    }

    switch (preset) {
      case 'carousel':
        return <CarouselWrapper CardComponent={SplitCard} />;
      case 'modern-glass':
        return <CarouselWrapper CardComponent={OverlayCard} />;
      case 'floating-circles':
        return <CarouselWrapper CardComponent={CircularCard} />;
      case 'large-gradient':
        return <CarouselWrapper CardComponent={BannerCard} />;
      default:
        return <CarouselWrapper CardComponent={OverlayCard} />;
    }
  };

  return (
    <div className={`${containerClasses} px-4 sm:px-6 lg:px-8`} style={containerStyle}>
      {renderContent()}
    </div>
  );
};

export default CategorySection;